<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { CalendarClock, MapPinned, RefreshCw, Route, UsersRound } from "lucide-vue-next";
import { api } from "@/lib/api";
import type { RouteType, Task } from "@/types";

const router = useRouter();
const tab = ref<RouteType>("highway");
const tasks = ref<Task[]>([]);
const loading = ref(true);
const error = ref("");
const visible = computed(() => tasks.value.filter((task) => task.routeType === tab.value));

async function load() {
  loading.value = true; error.value = "";
  try { tasks.value = await api.get<Task[]>("/tasks"); }
  catch (cause) { error.value = cause instanceof Error ? cause.message : "加载失败"; }
  finally { loading.value = false; }
}

onMounted(load);
</script>

<template>
  <section class="stack">
    <div class="hero">
      <h2>顺路跑一趟，实测一份真实能耗</h2>
      <p>仅限六型货车 · 领取后 24 小时内完成 · 奖励由运营在微信群发放</p>
    </div>
    <div class="segmented">
      <button :class="{ active: tab === 'highway' }" @click="tab = 'highway'">高速任务</button>
      <button :class="{ active: tab === 'national' }" @click="tab = 'national'">国道路段</button>
    </div>
    <div v-if="error" class="error-banner row between"><span>{{ error }}</span><button class="btn btn-sm" @click="load"><RefreshCw :size="14" />重试</button></div>
    <div v-if="loading" class="card state-box">正在获取可领取任务…</div>
    <div v-else-if="!visible.length" class="card state-box"><MapPinned :size="30" /><div>当前没有可领取任务</div></div>
    <article v-for="task in visible" :key="task.id" class="card task-card" :class="task.routeType" @click="router.push(`/tasks/${task.id}`)">
      <div class="row between">
        <h3 class="task-title">{{ task.title }}</h3>
        <span class="money">{{ (task.rewardCents / 100).toFixed(0) }}</span>
      </div>
      <div class="row" style="margin-top:8px"><span class="pill" :class="task.routeType === 'national' ? 'pill-success' : ''">{{ task.routeType === "highway" ? "高速任务" : "国道路段" }}</span><span class="small muted">{{ task.code }}</span></div>
      <div class="route-line"><span class="route-dot" />{{ task.originName }}<span class="route-dash" /><span class="route-dot end" />{{ task.destinationName }}</div>
      <div class="task-meta">
        <span><Route :size="14" /> {{ task.distanceKm }} km</span>
        <span><UsersRound :size="14" /> 剩余 {{ task.remainingSlots }} 名</span>
        <span><CalendarClock :size="14" /> 领后 24 小时</span>
      </div>
    </article>
  </section>
</template>

<style scoped>
.task-meta span { display: inline-flex; align-items: center; gap: 4px; }
</style>
